import { useState, type ReactNode } from 'react';
import { requestAssertion, wasDismissed } from '../lib/webauthn.ts';
import { partnerSecurityApi, PartnerApiError, type PartnerChallengeOption } from '../api/partner.ts';
import { Alert, Button, TextInput } from './ui.tsx';

/**
 * "Confirm it's you", for the two partner screens that need it.
 *
 * The Worker guards a small set of actions on a DELIBERATE proof made in the
 * last few minutes — changing the payout address above all — and answers
 * `FRESH_AUTH_REQUIRED` when the session cannot show one. This is the browser
 * half of that exchange, shared by the security screen and the account screen
 * so the two cannot drift apart.
 *
 * `MFA_SETUP_REQUIRED` is the other half of the same 403 and is deliberately
 * NOT handled here: it means the account holds no factor at all, and no dialog
 * can fix that. Callers surface it as a sentence pointing at enrollment.
 */

export function isFreshAuthRequired(error: unknown): boolean {
  return error instanceof PartnerApiError && error.code === 'FRESH_AUTH_REQUIRED';
}

export function isMfaSetupRequired(error: unknown): boolean {
  return error instanceof PartnerApiError && error.code === 'MFA_SETUP_REQUIRED';
}

interface ElevationState {
  options: PartnerChallengeOption[];
  option: PartnerChallengeOption;
  challengeId: string | null;
}

export interface PartnerElevation {
  /** Render this wherever the prompt belongs; null when nothing is pending. */
  panel: ReactNode;
  /**
   * Ask for a fresh proof, then run `retry` once it lands. Call it from an
   * error handler that saw `FRESH_AUTH_REQUIRED`.
   */
  begin: (retry?: () => void) => void;
  /** True while a proof is being collected. */
  pending: boolean;
}

export function usePartnerElevation(): PartnerElevation {
  const [state, setState] = useState<ElevationState | null>(null);
  const [resume, setResume] = useState<(() => void) | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setState(null);
    setResume(null);
    setCode('');
    setError(null);
  }

  function begin(retry?: () => void) {
    setError(null);
    // Stored as a thunk: `setState` treats a bare function as an updater, and
    // passing the callback directly would call it instead of remembering it.
    setResume(() => retry ?? null);
    void (async () => {
      try {
        const { options } = await partnerSecurityApi.elevateOptions();
        if (!options.length) throw new Error('No security method is available on this account.');
        setState({ options, option: options[0]!, challengeId: null });
      } catch (caught) {
        setState(null);
        setError(caught instanceof Error ? caught.message : 'Could not start verification.');
      }
    })();
  }

  async function start(option: PartnerChallengeOption) {
    if (!state) return;
    setError(null);
    try {
      const started = await partnerSecurityApi.elevateChallenge({ type: option.type, methodId: option.methodId });
      setState({ ...state, option, challengeId: started.challengeId });
      if (option.type === 'webauthn' && started.publicKey) {
        const assertion = await requestAssertion(started.publicKey);
        if (assertion) await finish(started.challengeId, assertion);
      }
    } catch (caught) {
      // A person who closed their browser's security-key prompt has not hit an
      // error; saying so would be noise on a deliberate cancellation.
      if (!wasDismissed(caught)) {
        setError(caught instanceof PartnerApiError ? caught.message : 'Could not verify that method.');
      }
      setState((value) => (value ? { ...value, challengeId: null } : null));
    }
  }

  async function finish(challengeId: string, assertion?: Awaited<ReturnType<typeof requestAssertion>>) {
    try {
      await partnerSecurityApi.elevateVerify({ challengeId, ...(assertion ? { assertion } : { code }) });
      const action = resume;
      close();
      action?.();
    } catch (caught) {
      setError(caught instanceof PartnerApiError ? caught.message : 'That code could not be verified.');
    }
  }

  const panel =
    state || error ? (
      <section className="card space-y-3">
        <h2 className="font-semibold">Verify it’s you</h2>
        <p className="text-sm text-[color:var(--color-muted)]">
          This protects the parts of your account an attacker would want most.
        </p>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {state ? (
          <>
            <div className="flex flex-wrap gap-2">
              {state.options.map((option) => (
                <Button
                  key={`${option.type}:${option.methodId ?? 'none'}`}
                  variant={
                    state.option.type === option.type && state.option.methodId === option.methodId
                      ? 'primary'
                      : 'secondary'
                  }
                  onClick={() => void start(option)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {state.challengeId && state.option.type !== 'webauthn' ? (
              <div className="flex flex-wrap items-end gap-2">
                <TextInput
                  id="partner-elevate-code"
                  label={state.option.type === 'recovery' ? 'Recovery code' : 'Code'}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  inputMode={state.option.type === 'recovery' ? 'text' : 'numeric'}
                  autoComplete={state.option.type === 'recovery' ? 'off' : 'one-time-code'}
                />
                <Button variant="primary" onClick={() => void finish(state.challengeId!)}>
                  Verify
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
      </section>
    ) : null;

  return { panel, begin, pending: state !== null };
}
